import { useEffect } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';

import Container from '../components/Container';

/**
 * `/auth` — single dispatcher for Firebase Auth email actions.
 *
 * Configure the verification and password-reset email templates' Action URLs
 * to `https://djinncoach.com/auth`. Branch on the `mode` query param into the
 * existing `/verify` and `/reset` pages. The original query string (including
 * `oobCode`, `apiKey`, `lang`) is preserved verbatim.
 *
 * URL shapes Firebase sends:
 *   /auth?mode=verifyEmail&oobCode=...&apiKey=...
 *   /auth?mode=resetPassword&oobCode=...&apiKey=...
 *   /auth?mode=signIn&oobCode=...&apiKey=...           (we don't support yet)
 *   /auth?mode=recoverEmail&oobCode=...&apiKey=...     (we don't support yet)
 *
 * iOS Universal Links intercept this URL before the browser navigates (the
 * AASA file's `paths` include `/auth*`), so on a device with the Djinn app
 * installed this page is never rendered — the system hands the URL to the
 * app directly. This component only runs on desktop / web fallback.
 */
export default function AuthAction() {
  const [params] = useSearchParams();
  const mode = params.get('mode');
  const search = params.toString();

  useEffect(() => {
    // Surface unsupported modes in the console for debugging during rollout.
    if (mode && mode !== 'verifyEmail' && mode !== 'resetPassword') {
      console.warn('[auth] unsupported action mode');
    }
  }, [mode]);

  if (mode === 'verifyEmail') {
    return <Navigate to={`/verify?${search}`} replace />;
  }
  if (mode === 'resetPassword') {
    return <Navigate to={`/reset?${search}`} replace />;
  }

  return (
    <Container narrow>
      <div className="djinn-card p-8">
        <h1 className="text-2xl font-semibold tracking-tight text-djinn-text">
          Unsupported action
        </h1>
        <p className="mt-4 text-djinn-text-2">
          This link uses an action type that Djinn Coach doesn't handle on the web.
          Open the Djinn Coach app on your phone and try again, or request a fresh
          email.
        </p>
      </div>
    </Container>
  );
}
